// src/utils/remediationTaskHelpers.js
// Pure remediation-task helpers for Compliance.jsx.
// No API calls, React state, or UI rendering.

import {
  getTaskBlockReason as formatGetTaskBlockReason,
  isClosedTask as formatIsClosedTask,
  isTaskOverdue as formatIsTaskOverdue,
  normalizeText,
} from "./complianceFormatters";

import {
  asObject,
  isIamAccessKeyRule,
} from "./awsEvidenceContext";

export const isClosedTask = formatIsClosedTask;
export const isTaskOverdue = formatIsTaskOverdue;
export const getTaskBlockReason = formatGetTaskBlockReason;

export function getInvestigationMetadata(task = {}) {
  return asObject(task?.task_metadata?.investigation);
}

export function buildInvestigationDraftFromTask(task = {}) {
  const investigation = getInvestigationMetadata(task);

  return {
    investigation_status: investigation.investigation_status || "not_started",
    dependency_owner: investigation.dependency_owner || "",
    workload_name: investigation.workload_name || "",
    secret_location: investigation.secret_location || "",
    credential_location: investigation.credential_location || "",
    last_used_interpretation: investigation.last_used_interpretation || "",
    rotation_plan: investigation.rotation_plan || "",
    replacement_key_created: Boolean(investigation.replacement_key_created),
    replacement_key_slot: investigation.replacement_key_slot || "",
    replacement_key_created_at: investigation.replacement_key_created_at || "",
    replacement_key_id_hint: investigation.replacement_key_id_hint || "",
    old_key_retirement_action: investigation.old_key_retirement_action || "",
    workload_updated: Boolean(investigation.workload_updated),
    test_result: investigation.test_result || "",
    monitoring_window: investigation.monitoring_window || "",
    monitoring_result: investigation.monitoring_result || "",
    ready_for_validation_reason: investigation.ready_for_validation_reason || "",
    notes: investigation.notes || "",
    evidence_links: Array.isArray(investigation.evidence_links)
      ? investigation.evidence_links.join("\n")
      : investigation.evidence_links || "",
  };
}

export function hasInvestigationContent(investigation = {}) {
  if (!investigation || typeof investigation !== "object") return false;

  return Boolean(
    investigation.dependency_owner ||
      investigation.workload_name ||
      investigation.secret_location ||
      investigation.credential_location ||
      investigation.last_used_interpretation ||
      investigation.rotation_plan ||
      investigation.test_result ||
      investigation.monitoring_window ||
      investigation.monitoring_result ||
      investigation.ready_for_validation_reason ||
      investigation.notes ||
      investigation.replacement_key_created ||
      investigation.replacement_key_slot ||
      investigation.replacement_key_created_at ||
      investigation.replacement_key_id_hint ||
      investigation.old_key_retirement_action ||
      investigation.workload_updated ||
      (Array.isArray(investigation.evidence_links) && investigation.evidence_links.length > 0)
  );
}

export function taskNeedsCredentialInvestigation(task = {}) {
  return isIamAccessKeyRule(task?.rule_id || task?.ruleId);
}

export function getInvestigationReadinessIssues(task = {}, draft = {}) {
  if (!taskNeedsCredentialInvestigation(task)) return [];

  const issues = [];
  const status = normalizeText(draft?.investigation_status || "");
  const statusReady = ["ready_for_validation", "rotation_complete"].includes(status);

  if (!statusReady) {
    issues.push("Set Investigation Status to Ready For Validation or Rotation Complete.");
  }

  if (!String(draft?.last_used_interpretation || "").trim()) {
    issues.push("Add a Last-Used Interpretation.");
  }

  if (!String(draft?.rotation_plan || "").trim()) {
    issues.push("Add a Rotation Plan.");
  }

  const replacementKeyCreated = Boolean(draft?.replacement_key_created);
  const oldKeyRetirementAction = normalizeText(draft?.old_key_retirement_action || "");
  const oldKeyRetirementActionComplete = Boolean(
    oldKeyRetirementAction && oldKeyRetirementAction !== "pending_rotation"
  );

  if (!replacementKeyCreated && !oldKeyRetirementActionComplete) {
    issues.push("Record either Replacement Key Created or a completed Old Key Retirement Action.");
  }

  if (replacementKeyCreated) {
    if (!normalizeText(draft?.replacement_key_slot || "")) {
      issues.push("Record the Replacement Key Slot.");
    }
    if (!String(draft?.replacement_key_created_at || "").trim()) {
      issues.push("Record when the replacement key was created or rotated.");
    }
    if (!Boolean(draft?.workload_updated)) {
      issues.push("Confirm the workload or secret was updated.");
    }
    if (!String(draft?.test_result || "").trim()) {
      issues.push("Add a Test Result for the replacement key.");
    }
  }

  if (!String(draft?.ready_for_validation_reason || "").trim()) {
    issues.push("Add a Ready for Validation Reason.");
  }

  return issues;
}

export function buildReadyForValidationBlockedMessage(task = {}, draft = {}) {
  const issues = getInvestigationReadinessIssues(task, draft);
  if (!issues.length) return "";
  return `Ready for Validation blocked until rotation evidence is complete: ${issues.join(" ")}`;
}

export function canShowReadyForValidationNextStep(task = {}, draft = {}) {
  if (!task?.id || isClosedTask(task)) return false;

  const taskStatus = normalizeText(task.status);
  const validationStatus = normalizeText(task.validation_status);
  const investigationStatus = normalizeText(draft?.investigation_status || "");

  if (["ready_for_validation", "validated", "closed"].includes(taskStatus)) return false;
  if (validationStatus === "passed") return false;
  if (!["rotation_complete", "ready_for_validation"].includes(investigationStatus)) return false;

  return getInvestigationReadinessIssues(task, draft).length === 0;
}

export function getValidationPendingGuidance(task = {}, draft = {}) {
  if (normalizeText(task?.validation_status) !== "pending") return "";

  const investigationStatus = normalizeText(draft?.investigation_status || "");
  if (["rotation_complete", "ready_for_validation"].includes(investigationStatus)) {
    return "Validation has not run yet. Mark the task Ready for Validation, then run Full Cloud Assessment so OASYS can verify the source evidence.";
  }

  return "Validation has not run yet. Complete the investigation evidence before submitting this task for validation.";
}

export function normalizeRemediationTasks(payload) {
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

export function normalizeRemediationTaskEvents(payload) {
  if (Array.isArray(payload?.events)) return payload.events;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

export function sortTaskEventsNewestFirst(events = []) {
  return [...events].sort((left, right) => {
    const leftTime = new Date(left?.created_at || 0).getTime();
    const rightTime = new Date(right?.created_at || 0).getTime();

    if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) return 0;
    if (Number.isNaN(leftTime)) return 1;
    if (Number.isNaN(rightTime)) return -1;

    return rightTime - leftTime;
  });
}

export function getTaskSortTimestamp(task = {}) {
  const candidates = isClosedTask(task)
    ? [task.closed_at, task.updated_at, task.validated_at, task.created_at]
    : [task.updated_at, task.created_at, task.due_date];

  for (const candidate of candidates) {
    const time = new Date(candidate || 0).getTime();
    if (!Number.isNaN(time) && time > 0) return time;
  }

  return 0;
}

export function sortRemediationTasksNewestFirst(tasks = []) {
  return [...tasks].sort((left, right) => {
    const rightTime = getTaskSortTimestamp(right);
    const leftTime = getTaskSortTimestamp(left);

    if (rightTime !== leftTime) return rightTime - leftTime;

    return String(right?.id || "").localeCompare(String(left?.id || ""));
  });
}

export function taskMatchesItem(task = {}, item = {}, frameworkFallback = "") {
  if (!task || !item) return false;

  const taskFramework = normalizeText(task.framework);
  const itemFramework = normalizeText(item.framework || frameworkFallback);
  const taskControl = normalizeText(task.control_id);
  const itemControl = normalizeText(item.control_id || item.controlId);
  const taskRule = normalizeText(task.rule_id);
  const itemRule = normalizeText(item.rule_id || item.ruleId);
  const taskResource = normalizeText(task.resource_id);
  const itemResource = normalizeText(item.resource_id || item.resourceId);

  if (taskFramework && itemFramework && taskFramework !== itemFramework) return false;
  if (!taskControl || !itemControl || taskControl !== itemControl) return false;

  if (taskRule && itemRule && taskRule !== itemRule) return false;
  if (taskRule && !itemRule) return false;

  if (taskResource && itemResource && taskResource !== itemResource) return false;

  return true;
}

export function findTaskForItem(tasks = [], item = {}, frameworkFallback = "") {
  return (
    tasks.find((task) => taskMatchesItem(task, item, frameworkFallback)) ||
    null
  );
}
